import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDT } from "@/lib/timefmt";
import { toast } from "sonner";
import { SortTh, FilterTh, filterRows, sortRows } from "@/components/tableTools";

const EVENT_LABELS = {
  password_changed_self: "Password changed (self)",
  admin_password_set: "Password set by admin",
  reset_link_sent: "Reset link emailed",
  reset_completed: "Reset completed",
  recovery_email_updated: "Recovery email updated",
  user_invited: "User invited",
  digest_settings_updated: "Digest settings updated",
  security_log_cleared: "Security log cleared",
};

export default function SecurityLogTab() {
  const { user } = useAuth();
  const isSuper = (user?.role === "admin" && !user?.admin_level) || user?.admin_level === "global";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [logs, setLogs] = useState([]);
  const [eventFilter, setEventFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [sort, setSort] = useState({ key: "when", dir: "desc" });
  const [qf, setQf] = useState({ when: "", event: "", user: "", by: "", detail: "" });

  const load = () => {
    if (!companyId) return;
    api.get(`/admin/security-log?company_id=${companyId}`).then((r) => setLogs(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      setCompanyId((prev) => prev || (isSuper ? r.data[0]?.id : user?.company_id) || "");
    }).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const eventTypes = [...new Set(logs.map((l) => l.event))].sort();
  const filtered = eventFilter ? logs.filter((l) => l.event === eventFilter) : logs;

  const logText = (l) => ({
    when: fmtDT(l.created_at),
    event: EVENT_LABELS[l.event] || l.event,
    user: `${l.target_name || ""} ${l.target_email || ""}`,
    by: l.actor_email || "self/link",
    detail: l.detail || "",
  });
  const logKey = (l, k) => (k === "when" ? l.created_at || "" : logText(l)[k] || "");
  const shown = sortRows(filterRows(filtered, logText, qf), sort, logKey);

  const clearLog = async () => {
    if (!window.confirm(`Clear ALL security log events for this company? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/security-log?company_id=${companyId}`);
      toast.success(`Cleared ${data.deleted} event(s)`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      {isSuper && companies.length > 1 && (
        <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60 max-w-sm">
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Company</label>
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="securitylog-company-select"
            className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}
      <div className="p-4 rounded-lg border border-slate-800 bg-slate-900/60 flex flex-wrap items-end gap-3">
        <div className="w-64">
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Event type</label>
          <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} data-testid="securitylog-event-filter"
            className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
            <option value="">All events</option>
            {eventTypes.map((ev) => <option key={ev} value={ev}>{EVENT_LABELS[ev] || ev}</option>)}
          </select>
        </div>
        <button onClick={clearLog} disabled={busy || logs.length === 0} data-testid="securitylog-clear-btn"
          className="px-3 py-2.5 rounded-md bg-rose-600/20 hover:bg-rose-600/30 text-rose-300 border border-rose-600/40 text-xs font-medium transition-colors disabled:opacity-40">
          {busy ? "Clearing…" : "Clear log"}
        </button>
      </div>
      <div className="rounded-lg border border-slate-800 bg-slate-900/60 overflow-hidden" data-testid="security-log-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-left">
                <SortTh label="When" k="when" sort={sort} setSort={setSort} />
                <SortTh label="Event" k="event" sort={sort} setSort={setSort} />
                <SortTh label="User" k="user" sort={sort} setSort={setSort} />
                <SortTh label="By" k="by" sort={sort} setSort={setSort} />
                <SortTh label="Detail" k="detail" sort={sort} setSort={setSort} />
              </tr>
              <tr className="border-b border-slate-800 text-left">
                <FilterTh value={qf.when} onChange={(v) => setQf((f) => ({ ...f, when: v }))} testId="securitylog-qfilter-when" />
                <FilterTh value={qf.event} onChange={(v) => setQf((f) => ({ ...f, event: v }))} testId="securitylog-qfilter-event" />
                <FilterTh value={qf.user} onChange={(v) => setQf((f) => ({ ...f, user: v }))} testId="securitylog-qfilter-user" />
                <FilterTh value={qf.by} onChange={(v) => setQf((f) => ({ ...f, by: v }))} testId="securitylog-qfilter-by" />
                <FilterTh value={qf.detail} onChange={(v) => setQf((f) => ({ ...f, detail: v }))} testId="securitylog-qfilter-detail" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70">
              {shown.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No security events recorded yet.</td></tr>
              )}
              {shown.map((l) => (
                <tr key={l.id} className="hover:bg-slate-900/60" data-testid={`securitylog-row-${l.id}`}>
                  <td className="px-4 py-3 font-mono-num text-xs text-slate-300">{fmtDT(l.created_at)}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-200 border border-slate-700 text-[10px] uppercase tracking-widest font-mono-num">
                      {EVENT_LABELS[l.event] || l.event}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">{l.target_name || "—"}</div>
                    <div className="text-xs text-slate-500">{l.target_email}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400">{l.actor_email || "self/link"}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 font-mono-num">{l.detail || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
